import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSetRecoilState } from "recoil";
import { CardElement } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons";
import {
  errorState,
  useCreateMutation,
  usePayMutation,
} from "./Settings.state";
import { Spinner, RowInfo, FormAutocomplete } from "../common";
import { Button, ButtonWrapper, Col, Row } from "../App.styles";
import { LicenseTypeModal, PaymentTeamsModal } from "./modals";
import { useHandleSubmitPayment } from "../lib/usePayment";

const PaymentForm = styled.form`
  width: 100%;
  display: flex;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;

    .col-md-2 {
      margin-top: 1rem;
    }
  }

  .col-lg-4 {
    justify-content: flex-end;
  }

  .StripeElement {
    width: 100%;
  }
`;

const CostRow = styled(Row)`
  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    margin-top: 1rem;
    margin-bottom: 2rem;
  }

  @media (min-width: ${({ theme }) => theme.minDesktopWidth}) {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
`;

const PaymentCol = styled(Col)`
  border-bottom: 1px solid #bdbdbd;
  padding-bottom: 0.75rem;
`;

const stripeStyles = {
  style: {
    base: {
      fontSize: "16px",
    },
  },
};

const Payment = ({ useQuery, payload }) => {
  const minimumCommercialTeam = 10
  const { t } = useTranslation();
  const { game_id } = useParams();
  const history = useHistory();
  const [collection, setCollection] = useQuery();
  const { license_type, purchased_players } = collection;
  const [license, setLicense] = useState(license_type);
  const isCommercial = useMemo(() => license === 1, [license])
  const [cost, setCost] = useState(0);
  const [, setDisabled] = useState(true);
  const [paid, setPaid] = useState(false);
  const [teams, setTeams] = useState(license_type === 1 ? minimumCommercialTeam : purchased_players);
  const { isCreating, create } = useCreateMutation();
  const mutation = usePayMutation();
  const { isPaying } = mutation;
  const setErrors = useSetRecoilState(errorState(game_id));
  const onCreate = async () => {
    const { data } = await create();
    history.push(`/tasks/organize/${data.id}`);
  };

  const handleSubmit = useHandleSubmitPayment({
    payload,
    onError: (error) => {
      console.error("[Teams] Payment Error: ", error);
      setErrors([error.message]);
    }, 
    onSuccess: () => {
      setPaid(true);
    }, 
    mutation, 
    params: {
      cost,
      purchased_players: teams,
    }
  })

  useEffect(() => setCost(updateCosts(license, teams)), [license, teams]);
  useEffect(
    () =>
      setDisabled(parseInt(license_type) === 1 || parseInt(license_type) === 3),
    [license_type]
  );
  useEffect(() => {
    const { license_type, purchased_players } = collection;

    if (
      game_id !== "new" &&
      !checkPurchasedPlayers(license_type, purchased_players)
    ) {
      setCollection({ ...collection, purchased_players: 1 });
    }
  }, [collection, game_id, setCollection]);
  const teamsOptions = useMemo(() => {
    return [
      1,
      2,
      3,
      4,
      5,
      10,
      15,
      20,
      50,
      100,
      250,
      500,
      1000,
      10000,
      100000,
      1000000,
    ]
      .map(i => ({ 
        id: i, 
        name: String(i),
        disabled: isCommercial
          ? i < 10
          : false
      }))
  }, [isCommercial])

  const getMinimumTeam = useCallback((selectedLicense) => {
    return selectedLicense === 1 ? minimumCommercialTeam : purchased_players
  }, [purchased_players, minimumCommercialTeam])

  useEffect(() => {
    // If Commercial is selected, the picker for teams 
    // can't ever be less than 10.
    setTeams(minimumCommercialTeam)
  }, [minimumCommercialTeam])

  return (
    <>
      <RowInfo className="StripeElement" help={<PaymentTeamsModal />}>
        <FormAutocomplete
          desc={t('Teams beyond this limit will be stopped from joining.')}
          label={t('Teams')}
          value={teams}
          onChange={(e, v) => setTeams(v?.id)}
          stateful={false}
          size="small"
          options={teamsOptions}
          // minimum team quantity depends on the license type,
          // using license as the key to trigger re-render
          key={license}
          creatable
        />
      </RowInfo>
      <RowInfo help={<LicenseTypeModal />}>
        <FormAutocomplete
          desc={t('Discounts apply for non-commerical usage.')}
          label={t('License Type')}
          value={license}
          onChange={(e, v) => {
            setLicense(v?.id)
            setTeams(getMinimumTeam(v?.id))
          }}
          stateful={false}
          size="small"
          options={[
            {
              id: 1,
              name: t('Commercial'),
            },
            {
              id: 2,
              name: t('Personal (Non-Commercial)'),
            },
            {
              id: 3,
              name: t('Educational'),
            },
            {
              id: 4,
              name: t('Not for Profit'),
            },
          ]}
        />
      </RowInfo>
      <CostRow>
        <Col size="10">
          <label className="form-label">Cost</label>${cost} USD
        </Col>
        <Col size="2"></Col>
      </CostRow>
      <Row>
        {!!cost && (
          <PaymentForm onSubmit={handleSubmit}>
            <PaymentCol md="10" size="12">
              <CardElement options={stripeStyles} />
            </PaymentCol>
            <Col md="2" size="12">
              <Button type="submit" color="accent1">
                {t('Pay Now')} {isPaying && <Spinner size="0.875rem" />}
              </Button>
            </Col>
          </PaymentForm>
        )}
        {paid ? (
          <img
            alt=""
            src={`${process.env.REACT_APP_CONVERSION_PAY}&value==${
              cost * 100
            }&currency_code=${process.env.REACT_APP_CURRENCY_CODE}`}
            style={{ width: "1px", height: "1px" }}
          />
        ) : (
          <img
            alt=""
            src={process.env.REACT_APP_CONVERSION_BUILD}
            style={{ width: "1px", height: "1px" }}
          />
        )}
      </Row>
      <Row>
        <Col>
          <ButtonWrapper>
            {game_id === "new" && (
              <Button color="primary" onClick={onCreate}>
                Save{" "}
                {isCreating ? (
                  <Spinner size="0.875rem" />
                ) : (
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                )}
              </Button>
            )}
          </ButtonWrapper>
        </Col>
      </Row>
    </>
  );
};

export default Payment;

const checkPurchasedPlayers = (license, teams) => {
  if (parseInt(license) === 2 || parseInt(license) === 4) {
    return true;
  }

  if (parseInt(teams) === 1 || parseInt(teams) >= 10) {
    return true;
  }

  return false;
};

const updateCosts = (license, teams = 1) => {
  let rate;

  switch (parseInt(license)) {
    default:
      rate = 40.0;
      break;
    case 2:
      rate = 10.0;
      break;
    case 3:
      rate = 15.0;
      break;
    case 4:
      rate = 20.0;
      break;
  }

  if (parseInt(teams) === 1) {
    return 0;
  }

  return (parseFloat(teams) * rate).toFixed(2);
};
